import { useState } from 'react';
import type { LabelEnrolment } from '../dataPoliciesApi';

/** Enrolled labels referenced by data policies cannot be un-enrolled. */
export const isLabelInUse = (entry: LabelEnrolment): boolean =>
  entry.rbacEnrolled && entry.policies.length > 0;

/**
 * Tracks the user's toggles against the enrolment they were shown. Only those
 * changes are saved, so labels enrolled or un-enrolled meanwhile by someone
 * else are left alone (no last-writer-wins over the whole set).
 */
export const useEnrolmentDraft = (entries: readonly LabelEnrolment[]) => {
  const [shown] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        entries.filter((entry) => entry.rbacEnrolled).map((entry) => entry.id),
      ),
  );
  const [draft, setDraft] = useState<ReadonlySet<string>>(shown);

  const toggle = (entry: LabelEnrolment, enrolled: boolean) => {
    if (!enrolled && isLabelInUse(entry)) {
      return;
    }
    setDraft((current) => {
      const next = new Set(current);
      if (enrolled) {
        next.add(entry.id);
      } else {
        next.delete(entry.id);
      }
      return next;
    });
  };

  const enrol = [...draft].filter((id) => !shown.has(id));
  const unenrol = [...shown].filter((id) => !draft.has(id));
  return {
    draft,
    toggle,
    isDirty: enrol.length > 0 || unenrol.length > 0,
    enrol,
    unenrol,
  };
};
